import React from 'react';
import {
    Box,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Typography,
    Chip,
    Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getBookingStatusColor } from '../helpers/getBookingStatusColor';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';

const GroupTable = ({ items, showShowColumn, renderSummary, isSmall }) => {
    const { t } = useTranslation();
    const sort = useSort(items, 'bookingId', 'asc');

    const renderHeader = () => (
        <TableRow>
            <SortableHeaderCell field="bookingId" sort={sort}>
                {t('reports.columns.bookingId')}
            </SortableHeaderCell>
            <SortableHeaderCell field="status" sort={sort}>
                {t('reports.columns.bookingStatus')}
            </SortableHeaderCell>
            {showShowColumn && (
                <SortableHeaderCell field="showTitle" sort={sort}>
                    {t('reports.columns.show', 'Show')}
                </SortableHeaderCell>
            )}
            <SortableHeaderCell field="contact" sort={sort}>
                {t('reports.columns.contact')}
            </SortableHeaderCell>
            <SortableHeaderCell field="voucher" sort={sort}>
                {t('reports.columns.voucher')}
            </SortableHeaderCell>
            <SortableHeaderCell field="customers" sort={sort}>
                {t('reports.columns.customers')}
            </SortableHeaderCell>
            <SortableHeaderCell field="isCheckedIn" sort={sort}>
                {t('reports.manifest.columns.checkedIn', 'Checked In')}
            </SortableHeaderCell>
            <SortableHeaderCell field="seatingGroup" sort={sort}>
                {t('reports.manifest.columns.seatingGroup', 'Seating Group')}
            </SortableHeaderCell>
            <SortableHeaderCell field="seats" sort={sort}>
                {t('reports.manifest.columns.seats')}
            </SortableHeaderCell>
            <SortableHeaderCell field="availability" sort={sort}>
                {t('reports.columns.showDate')}
            </SortableHeaderCell>
            <SortableHeaderCell field="bookingDate" sort={sort}>
                {t('reports.manifest.columns.createdAt', 'Created at')}
            </SortableHeaderCell>
            <SortableHeaderCell field="affiliateName" sort={sort}>
                {t('reports.manifest.columns.affiliate', 'Affiliate')}
            </SortableHeaderCell>
            <SortableHeaderCell field="totalPrice" sort={sort} align="right">
                {t('reports.manifest.columns.total', 'Total')}
            </SortableHeaderCell>
            <SortableHeaderCell field="unpaidAmount" sort={sort} align="right">
                {t('reports.manifest.columns.unpaid', 'Unpaid')}
            </SortableHeaderCell>
            <TableCell>{t('reports.manifest.columns.notes', 'Notes')}</TableCell>
        </TableRow>
    );

    const renderBookingRow = (item) => (
        <TableRow key={item.bookingId}>
            <TableCell>{item.bookingId}</TableCell>
            <TableCell>
                <Chip
                    label={t(`bookingstatus.${item.status}`)}
                    color={getBookingStatusColor(item.status)}
                    size="small"
                />
            </TableCell>
            {showShowColumn && <TableCell>{item.showTitle}</TableCell>}
            <TableCell>
                {item.contact}
                <br />
                <Typography variant="caption" color="text.secondary">
                    {item.phone}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                    {item.email}
                </Typography>
            </TableCell>
            <TableCell>{item.voucher}</TableCell>
            <TableCell>{item.customers}</TableCell>
            <TableCell>
                {item.isCheckedIn
                    ? t('reports.manifest.yes', 'Yes')
                    : t('reports.manifest.no', 'No')}
            </TableCell>
            <TableCell>
                <Typography variant="caption">{item.seatingGroup || ''}</Typography>
            </TableCell>
            <TableCell>{item.seats}</TableCell>
            <TableCell>{item.availability}</TableCell>
            <TableCell>
                <Typography variant="caption">
                    {item.bookingDate ? formatDateTimeInVenueTz(item.bookingDate) : ''}
                </Typography>
            </TableCell>
            <TableCell>
                <Typography variant="caption">{item.affiliateName || ''}</Typography>
            </TableCell>
            <TableCell align="right">{formatCentsToUsd(item.totalPrice)}</TableCell>
            <TableCell align="right">
                {item.unpaidAmount ? (
                    <Typography component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                        {formatCentsToUsd(item.unpaidAmount)}
                    </Typography>
                ) : (
                    ''
                )}
            </TableCell>
            <TableCell>
                <Typography
                    variant="caption"
                    sx={{ maxWidth: 200, display: 'block', whiteSpace: 'pre-wrap' }}
                >
                    {item.checkinNote || item.agentNotes || ''}
                </Typography>
            </TableCell>
        </TableRow>
    );

    return (
        <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table
                stickyHeader
                sx={{ width: '100%', '& thead th': { top: 0 } }}
                size={isSmall ? 'small' : 'medium'}
            >
                <TableHead>{renderHeader()}</TableHead>
                <TableBody>
                    {sort.sortedItems.map(renderBookingRow)}
                    {renderSummary && renderSummary()}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ManifestReportTable = ({ items, groups, showId }) => {
    const { t } = useTranslation();
    const showShowColumn = !showId;

    const renderSummaryRow = ({
        label,
        bgColor,
        bookingCount,
        customerCount,
        checkedInCount,
        notCheckedInCount,
        totalPrice: price,
        unpaidTotal,
        breakdown,
    }) => (
        <TableRow sx={{ backgroundColor: bgColor }}>
            <TableCell colSpan={showShowColumn ? 3 : 2}>
                <strong>{label}</strong>
            </TableCell>
            <TableCell>
                <strong>
                    {bookingCount} {t('reports.manifest.bookings', 'bookings')}
                </strong>
            </TableCell>
            <TableCell />
            <TableCell>
                <strong>
                    {customerCount} {t('reports.manifest.customers', 'customers')}
                </strong>
                {breakdown && (
                    <>
                        <br />
                        <Typography variant="caption">{breakdown}</Typography>
                    </>
                )}
            </TableCell>
            <TableCell>
                <strong>
                    {checkedInCount} {t('reports.manifest.checkedIn', 'checked in')}
                    {notCheckedInCount > 0 && `, ${notCheckedInCount} not`}
                </strong>
            </TableCell>
            <TableCell />
            <TableCell>
                <strong>
                    {customerCount} {t('reports.manifest.seated', 'seated')}
                </strong>
            </TableCell>
            {/* showDate */}
            <TableCell />
            {/* createdAt, affiliate */}
            <TableCell colSpan={2} />
            <TableCell align="right">
                <strong>{formatCentsToUsd(price)}</strong>
            </TableCell>
            <TableCell align="right">
                {unpaidTotal ? (
                    <Typography component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                        {formatCentsToUsd(unpaidTotal)}
                    </Typography>
                ) : (
                    ''
                )}
            </TableCell>
            {/* notes */}
            <TableCell />
        </TableRow>
    );

    // Grouped view
    if (groups && groups.length > 0) {
        const totals = groups.reduce(
            (s, g) => ({
                bookings: s.bookings + g.bookingCount,
                customers: s.customers + g.customerCount,
                checkedIn: s.checkedIn + g.checkedInCount,
                notCheckedIn: s.notCheckedIn + g.notCheckedInCount,
                price: s.price + g.totalPrice,
                unpaid: s.unpaid + (g.unpaidTotal || 0),
            }),
            { bookings: 0, customers: 0, checkedIn: 0, notCheckedIn: 0, price: 0, unpaid: 0 },
        );
        const showGrandTotal = groups.length > 1;

        return (
            <Stack spacing={3} width="100%">
                {groups.map((group, index) => (
                    <Box key={group.ticketTypeName}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {group.ticketTypeName}
                        </Typography>
                        <Box>
                            <GroupTable
                                items={group.items}
                                showShowColumn={showShowColumn}
                                isSmall={true}
                                renderSummary={() => (
                                    <>
                                        {renderSummaryRow({
                                            label: t('reports.summary.groupTotal'),
                                            bgColor: 'action.hover',
                                            bookingCount: group.bookingCount,
                                            customerCount: group.customerCount,
                                            checkedInCount: group.checkedInCount,
                                            notCheckedInCount: group.notCheckedInCount,
                                            totalPrice: group.totalPrice,
                                            unpaidTotal: group.unpaidTotal,
                                            breakdown: group.ticketOptionBreakdown
                                                .map((opt) => `${opt.count} ${opt.name}`)
                                                .join(', '),
                                        })}
                                        {showGrandTotal &&
                                            index === groups.length - 1 &&
                                            renderSummaryRow({
                                                label: t('reports.summary.grandTotal'),
                                                bgColor: 'action.selected',
                                                bookingCount: totals.bookings,
                                                customerCount: totals.customers,
                                                checkedInCount: totals.checkedIn,
                                                notCheckedInCount: totals.notCheckedIn,
                                                totalPrice: totals.price,
                                                unpaidTotal: totals.unpaid,
                                            })}
                                    </>
                                )}
                            />
                        </Box>
                    </Box>
                ))}
            </Stack>
        );
    }

    // Fallback: flat table
    return (
        <Box sx={{ width: '100%' }}>
            <GroupTable items={items} showShowColumn={showShowColumn} />
        </Box>
    );
};

export default ManifestReportTable;
